import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

interface SignedUrlState {
  url: string | null;
  isLoading: boolean;
  error: string | null;
  isFallback: boolean;
  isPreview: boolean;
}

interface CacheEntry {
  url: string;
  expiresAt: number;
  isFallback: boolean;
  isPreview: boolean;
}

// In-memory cache for signed URLs
const urlCache = new Map<string, CacheEntry>();

// Cache duration buffer (refresh 2 minutes before expiry for full access, no buffer for preview)
const CACHE_BUFFER_MS = 2 * 60 * 1000;

export function useSignedAudioUrl(
  trackId: string | null,
  fallbackUrl: string | null,
) {
  // Check cache synchronously BEFORE any async operations to prevent race conditions
  const getCachedState = (): SignedUrlState => {
    if (!trackId) {
      return {
        url: null,
        isLoading: false,
        error: null,
        isFallback: false,
        isPreview: false,
      };
    }

    const cached = urlCache.get(trackId);
    const bufferMs = cached?.isPreview ? 5000 : CACHE_BUFFER_MS;

    if (cached && cached.expiresAt > Date.now() + bufferMs) {
      return {
        url: cached.url,
        isLoading: false,
        error: null,
        isFallback: cached.isFallback,
        isPreview: cached.isPreview,
      };
    }

    // No valid cache - will need to fetch
    return {
      url: null,
      isLoading: true,
      error: null,
      isFallback: false,
      isPreview: false,
    };
  };

  const [state, setState] = useState<SignedUrlState>(getCachedState);

  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const fetchSignedUrl = useCallback(
    async (forceRefresh = false) => {
      if (!trackId) {
        setState({
          url: null,
          isLoading: false,
          error: null,
          isFallback: false,
          isPreview: false,
        });
        return;
      }

      // Check if user is authenticated
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const isAuthenticated = !!session;

      // Check cache first (unless forcing refresh)
      // For preview mode, use shorter cache check
      if (!forceRefresh) {
        const cached = urlCache.get(trackId);
        const bufferMs = cached?.isPreview ? 5000 : CACHE_BUFFER_MS; // 5 seconds for preview, 5 min for full

        if (cached && cached.expiresAt > Date.now() + bufferMs) {
          // If cached is preview but user is now authenticated, fetch fresh full URL
          if (cached.isPreview && isAuthenticated) {
            // Continue to fetch full URL
          } else {
            setState({
              url: cached.url,
              isLoading: false,
              error: null,
              isFallback: cached.isFallback,
              isPreview: cached.isPreview,
            });
            return;
          }
        }
      }

      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        // Prepare request body - request preview if not authenticated
        const requestBody: { track_id: string; preview?: boolean } = {
          track_id: trackId,
        };

        if (!isAuthenticated) {
          requestBody.preview = true;
          console.log("DRM: Requesting preview URL for track:", trackId);
        }

        const { data, error } = await supabase.functions.invoke(
          "get-signed-audio-url",
          {
            body: requestBody,
          },
        );

        if (!isMountedRef.current) return;

        if (error) {
          console.error("Error fetching signed URL:", error);

          // Check if it's a content restriction error
          const errorMessage = error.message || "";
          const isContentRestriction =
            errorMessage.includes("403") ||
            errorMessage.includes("not yet released") ||
            errorMessage.includes("verified users");

          setState({
            url: null,
            isLoading: false,
            error: isContentRestriction
              ? "Content not available"
              : error.message,
            isFallback: false,
            isPreview: false,
          });
          return;
        }

        if (data?.signedUrl) {
          const expiresIn = data.expiresIn || 3600;
          const isFallback = data.fallback || false;
          const isPreview = data.isPreview || false;

          // Cache the URL
          urlCache.set(trackId, {
            url: data.signedUrl,
            expiresAt: Date.now() + expiresIn * 1000,
            isFallback,
            isPreview,
          });

          setState({
            url: data.signedUrl,
            isLoading: false,
            error: null,
            isFallback,
            isPreview,
          });

          // Schedule refresh before expiry (only for full access URLs)
          if (!isFallback && !isPreview && expiresIn > 0) {
            const refreshIn = Math.max((expiresIn - 120) * 1000, 60000); // Refresh 2 min before expiry, at least 1 minute

            if (refreshTimerRef.current) {
              clearTimeout(refreshTimerRef.current);
            }

            refreshTimerRef.current = setTimeout(() => {
              if (isMountedRef.current) {
                fetchSignedUrl(true);
              }
            }, refreshIn);
          }
        } else {
          // No signed URL returned
          setState({
            url: null,
            isLoading: false,
            error: "No audio URL available",
            isFallback: false,
            isPreview: false,
          });
        }
      } catch (err) {
        console.error("Exception fetching signed URL:", err);
        if (!isMountedRef.current) return;

        setState({
          url: null,
          isLoading: false,
          error: err instanceof Error ? err.message : "Unknown error",
          isFallback: false,
          isPreview: false,
        });
      }
    },
    [trackId],
  );

  // Fetch signed URL when track changes
  useEffect(() => {
    isMountedRef.current = true;
    fetchSignedUrl();

    return () => {
      isMountedRef.current = false;
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [fetchSignedUrl]);

  // Manual refresh function
  const refresh = useCallback(() => {
    fetchSignedUrl(true);
  }, [fetchSignedUrl]);

  return {
    audioUrl: state.url,
    isLoading: state.isLoading,
    error: state.error,
    isFallback: state.isFallback,
    isPreview: state.isPreview,
    refresh,
  };
}

// Utility to clear cache (useful for testing/debugging)
export function clearSignedUrlCache() {
  urlCache.clear();
}
